import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./App.css";

import SplashScreen from "./components/SplashScreen";
import Welcome from "./pages/Welcome";
import LearnToHunt from "./pages/LearnToHunt";
import Home from "./pages/HomeMapbox";
import PhotoBooth from "./pages/PhotoBooth";
import FindMyRoute from "./pages/FindMyRoute";
import Wayfinder from "./pages/WayfinderRefactored";
import { DrawerProvider } from "./context/DrawerContext";
import { UserProvider, useUser } from "./context/UserContext";
import { getFlowConfig, isRouteEnabled } from "./config/flowConfig";
import "./services/firebase";

// Excel export functionality
const exportToExcel = async () => {
  try {
    // Dynamically import required modules
    const { collection, getDocs } = await import("firebase/firestore");
    const { db } = await import("./services/firebase");
    const XLSX = await import("xlsx");

    console.log("Starting Firestore data export...");

    // Define collections to export - the 2 collections that exist in firestore.rules
    const collections = ["users", "bibUsage"];

    const workbook = XLSX.utils.book_new();
    let exportedCollections = 0;

    for (const collectionName of collections) {
      try {
        console.log(`Exporting collection: ${collectionName}`);
        const collectionRef = collection(db, collectionName);
        const snapshot = await getDocs(collectionRef);

        if (!snapshot.empty) {
          const data = [];
          snapshot.forEach((doc) => {
            const docData = doc.data();
            // Add document ID to the data
            data.push({
              id: doc.id,
              ...docData,
              // Convert complex objects to strings for Excel compatibility
              ...Object.fromEntries(
                Object.entries(docData).map(([key, value]) => [
                  key,
                  typeof value === "object" && value !== null
                    ? JSON.stringify(value)
                    : value,
                ])
              ),
            });
          });

          // Create worksheet
          const worksheet = XLSX.utils.json_to_sheet(data);

          // Auto-size columns
          const columnWidths = [];
          data.forEach((row) => {
            Object.keys(row).forEach((key, index) => {
              const cellValue = String(row[key] || "");
              columnWidths[index] = Math.max(
                columnWidths[index] || 0,
                Math.min(cellValue.length, 50) // Cap at 50 characters
              );
            });
          });

          worksheet["!cols"] = columnWidths.map((width) => ({
            width: width + 2,
          }));

          // Add worksheet to workbook
          XLSX.utils.book_append_sheet(workbook, worksheet, collectionName);
          exportedCollections++;

          console.log(
            `✓ Exported ${data.length} documents from ${collectionName}`
          );
        } else {
          console.log(`⚠ Collection ${collectionName} is empty`);
        }
      } catch (error) {
        console.error(`✗ Error exporting collection ${collectionName}:`, error);
      }
    }

    if (exportedCollections > 0) {
      // Generate filename with timestamp
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-");
      const filename = `firestore_export_${timestamp}.xlsx`;

      // Save the file
      XLSX.writeFile(workbook, filename);

      console.log(
        `✅ Export completed! ${exportedCollections} collections exported to ${filename}`
      );

      // Show success message
      toast.success(
        `Data exported successfully! ${exportedCollections} collections exported.`
      );
    } else {
      console.log("❌ No collections were exported successfully");
      toast.error("Export failed: No collections could be exported");
    }
  } catch (error) {
    console.error("Export error:", error);
    toast.error("Export failed: " + error.message);
  }
};

// Auto-export Firestore data on page load (development only) for specific user
const useAutoExport = () => {
  const { userData } = useUser();

  useEffect(() => {
    // Only run in development mode and for specific user email
    if (
      userData?.email === "ak45hhh@gmail.com" &&
      process.env.NODE_ENV === "development"
    ) {
      console.log("🔄 Auto-exporting Firestore data for ak45hhh@gmail.com...");
      // Delay export to ensure Firebase is initialized
      const timer = setTimeout(() => {
        exportToExcel();
      }, 2000); // Wait 2 seconds for Firebase to initialize

      return () => clearTimeout(timer);
    }
  }, [userData?.email]);
};

// Navigation logger component
const NavigationLogger = () => {
  const location = useLocation();

  useEffect(() => {
    console.log("=== ROUTE CHANGE ===");
    console.log("New route:", location.pathname);
    console.log("Route state:", location.state);
    console.log("History length:", window.history.length);
    console.log("History state:", window.history.state);
    console.log("Current flow:", getFlowConfig().type);
    console.log("==================");
  }, [location]);

  return null;
};

// Loading component
const LoadingScreen = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      backgroundColor: "#f0f8ff",
    }}
  >
    <div
      style={{
        textAlign: "center",
        color: "#081F2D",
      }}
    >
      <div
        style={{
          width: "40px",
          height: "40px",
          border: "4px solid #e3f2fd",
          borderTop: "4px solid #1976d2",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
          margin: "0 auto 16px",
        }}
      ></div>
      <p>Loading...</p>
    </div>
  </div>
);

// App content with user context
const AppContent = () => {
  const { isLoading, isLoggedIn } = useUser();

  // Auto-export Firestore data in development mode
  useAutoExport();

  // Debug: Fetch and display all users from Firebase
  // useEffect(() => {
  //   const debugUsers = async () => {
  //     try {
  //       const { collection, getDocs } = await import("firebase/firestore");
  //       const { db } = await import("./services/firebase");
  //       const usersRef = collection(db, "users");
  //       const snapshot = await getDocs(usersRef);

  //       console.log("=== DEBUG: ALL USERS FROM FIREBASE ===");
  //       const usersWithChallengeScores = [];
  //       const usersWithTreasures = [];
  //       const allUsers = [];

  //       snapshot.forEach((doc) => {
  //         const userData = doc.data();
  //         const userId = doc.id;

  //         if (userData.totalBoosterScore > 0) {
  //           usersWithChallengeScores.push({ id: userId, ...userData });
  //         }
  //         if (userData.collectedTreasures?.length > 1) {
  //           usersWithTreasures.push({ id: userId, ...userData });
  //         }
  //         allUsers.push({ id: userId, ...userData });
  //       });

  //       // Find users with specific challenge scores > 5
  //       const usersWithHighScores = usersWithChallengeScores.filter((user) => {
  //         if (!user.challengeScores) return false;

  //         const matchTileScore = user.challengeScores["MATCH TILE"] || 0;
  //         const puzzleScore = user.challengeScores["PUZZLE"] || 0;
  //         const triviaScore = user.challengeScores["TRIVIA"] || 0;

  //         return matchTileScore > 5 || puzzleScore > 5 || triviaScore > 5;
  //       });

  //       // Find users with anomalous scores (not 2 or 5)
  //       const usersWithAnomalousScores = usersWithChallengeScores.filter(
  //         (user) => {
  //           if (!user.challengeScores) return false;

  //           const matchTileScore = user.challengeScores["MATCH TILE"] || 0;
  //           const puzzleScore = user.challengeScores["PUZZLE"] || 0;
  //           const triviaScore = user.challengeScores["TRIVIA"] || 0;

  //           // Check if any score is not 0, 2, or 5 (anomalous)
  //           const hasAnomalousScore = [
  //             matchTileScore,
  //             puzzleScore,
  //             triviaScore,
  //           ].some((score) => score !== 0 && score !== 2 && score !== 5);

  //           return hasAnomalousScore;
  //         }
  //       );

  //       // Verify if totalBoosterScore matches sum of individual challenge scores
  //       const scoreVerification = usersWithChallengeScores
  //         .map((user) => {
  //           if (!user.challengeScores) return null;

  //           const matchTileScore = user.challengeScores["MATCH TILE"] || 0;
  //           const puzzleScore = user.challengeScores["PUZZLE"] || 0;
  //           const triviaScore = user.challengeScores["TRIVIA"] || 0;

  //           const calculatedTotal = matchTileScore + puzzleScore + triviaScore;
  //           const actualTotal = user.totalBoosterScore || 0;
  //           const isCorrect = calculatedTotal === actualTotal;

  //           return {
  //             userId: user.id,
  //             email: user.email,
  //             matchTileScore,
  //             puzzleScore,
  //             triviaScore,
  //             calculatedTotal,
  //             actualTotal,
  //             isCorrect,
  //             difference: actualTotal - calculatedTotal,
  //           };
  //         })
  //         .filter(Boolean);

  //       const correctScores = scoreVerification.filter((v) => v.isCorrect);
  //       const incorrectScores = scoreVerification.filter((v) => !v.isCorrect);

  //       console.log(
  //         "Users with totalBoosterScore > 0:",
  //         usersWithChallengeScores.length
  //       );
  //       console.log(
  //         "Users with totalBoosterScore > 0 details:",
  //         usersWithChallengeScores
  //       );
  //       console.log(
  //         "Users with high challenge scores details:",
  //         usersWithHighScores.sort(
  //           (a, b) => b.totalBoosterScore - a.totalBoosterScore
  //         )
  //       );

  //       console.log(
  //         "Users with anomalous scores (not 0, 2, or 5):",
  //         usersWithAnomalousScores.length
  //       );
  //       console.log(
  //         "Users with anomalous scores details:",
  //         usersWithAnomalousScores
  //       );

  //       console.log("=== SCORE VERIFICATION ===");
  //       console.log("Total users verified:", scoreVerification.length);
  //       console.log("Users with correct total scores:", correctScores.length);
  //       console.log(
  //         "Users with incorrect total scores:",
  //         incorrectScores.length
  //       );

  //       if (incorrectScores.length > 0) {
  //         console.log("Users with score mismatches:", incorrectScores);
  //       }

  //       console.log("=== END SCORE VERIFICATION ===");

  //       console.log(
  //         "Users with collectedTreasures details:",
  //         usersWithTreasures
  //       );
  //       console.log("All users:", allUsers.length);
  //       console.log(
  //         "Users with collectedTreasures > 1:",
  //         usersWithTreasures.length
  //       );
  //       console.log(
  //         "Users with MATCH TILE/PUZZLE/TRIVIA scores > 5:",
  //         usersWithHighScores.length
  //       );
  //       console.log("=== END DEBUG ===");
  //     } catch (error) {
  //       console.error("Debug: Error fetching users:", error);
  //     }
  //   };

  //   if (process.env.NODE_ENV === "development") {
  //     debugUsers();
  //   }
  // }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  const flowConfig = getFlowConfig();
  console.log("App initialized with flow:", flowConfig.type);

  return (
    <div className="App">
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#363636",
            color: "#fff",
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: "#4ade80",
              secondary: "#fff",
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: "#ef4444",
              secondary: "#fff",
            },
          },
        }}
      />
      <Router>
        <NavigationLogger />
        <Routes>
          {/* Root path - redirect logged-in users to Home, others to SplashScreen */}
          <Route
            path="/"
            element={
              isLoggedIn ? <Navigate to="/home" replace /> : <SplashScreen />
            }
          />

          {/* Conditionally render routes based on flow configuration */}
          {isRouteEnabled("/welcome") && (
            <Route path="/welcome" element={<Welcome />} />
          )}
          {isRouteEnabled("/hunt") && (
            <Route path="/hunt" element={<LearnToHunt />} />
          )}
          {isRouteEnabled("/home") && <Route path="/home" element={<Home />} />}
          {isRouteEnabled("/photobooth") && (
            <Route path="/photobooth" element={<PhotoBooth />} />
          )}
          {isRouteEnabled("/find-my-route") && (
            <Route path="/find-my-route" element={<FindMyRoute />} />
          )}
          {isRouteEnabled("/wayfinder") && (
            <Route path="/wayfinder" element={<Wayfinder />} />
          )}

          {/* Fallback route - redirect logged-in users to Home, others to SplashScreen */}
          <Route
            path="*"
            element={
              isLoggedIn ? (
                <Navigate to="/home" replace />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
        </Routes>
      </Router>
    </div>
  );
};

function App() {
  return (
    <UserProvider>
      <DrawerProvider>
        <AppContent />
      </DrawerProvider>
    </UserProvider>
  );
}

export default App;
